import React, { useState } from 'react';
import { Mail, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';
import { apiWorker } from '../../api/worker';

interface PublicSubscribeWidgetProps {
  nodeId: string;
  nodeName: string;
  className?: string;
}

export const PublicSubscribeWidget: React.FC<PublicSubscribeWidgetProps> = ({
  nodeId,
  nodeName,
  className
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await apiWorker.post(`/api/pulse/${nodeId}/newsletters/subscribe`, { email });
      setIsSubscribed(true);
      toast.success('Subscription successful! Check your email to verify.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Subscription failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className={cn("bg-teal-50 border border-teal-200 rounded-2xl p-6 text-center shadow-sm", className)}>
        <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={24} />
        </div>
        <h3 className="text-sm font-bold text-teal-900 mb-1">You're Subscribed!</h3>
        <p className="text-xs text-teal-700">
          You'll now receive the latest updates from <span className="font-bold">{nodeName}</span> directly in your inbox.
        </p>
        <button 
          onClick={() => setIsSubscribed(false)}
          className="mt-4 text-[10px] font-bold text-teal-600 uppercase tracking-widest hover:underline"
        >
          Subscribe another email
        </button>
      </div>
    );
  }

  return (
    <div className={cn("subscribe-widget bg-white border border-gray-100 rounded-2xl p-6 shadow-xl relative overflow-hidden group dark:bg-slate-900", className)}>
      <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
        <Mail size={80} />
      </div>
      
      <div className="relative z-10">
        <h3 className="text-sm font-bold text-gray-900 mb-1 dark:text-white">Join our newsletter</h3>
        <p className="text-xs text-gray-500 mb-5 leading-relaxed">
          Stay updated with public announcements from <span className="font-semibold text-gray-700">{nodeName}</span>. No account required.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            placeholder="your@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="bg-gray-50 border-transparent focus:bg-white dark:bg-slate-900"
          />
          <Button 
            variant="primary" 
            className="w-full justify-center py-2.5 font-bold tracking-tight shadow-lg shadow-navy-900/20"
            isLoading={isLoading}
            type="submit"
          >
            Subscribe
          </Button>
        </form>

        <div className="mt-4 flex items-start gap-2 text-[10px] text-gray-400">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          <p>
            By subscribing, you agree to receive public newsletters. You can opt-out at any time via the link in every email.
          </p>
        </div>
      </div>
    </div>
  );
};
